import { useState, useEffect, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import api from "../services/api";
import toast from "react-hot-toast";

export default function AdminPendingReviews() {
  const [search, setSearch] = useState("");
  const [pendingEsmps, setPendingEsmps] = useState([]);
  const [selectedEsmp, setSelectedEsmp] = useState(null);
  const [reviewerId, setReviewerId] = useState("");
  const [deadline, setDeadline] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewers, setReviewers] = useState([]);

  const fetchPendingEsmps = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get("/esmp/status/Pending");
      const data = res?.data?.data ?? [];
      setPendingEsmps(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch pending ESMPs:", err);
      const msg = err.response?.data?.message || "Could not load pending ESMPs";
      toast.error(msg);
      setError(msg);
      setPendingEsmps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReviewers = useCallback(async () => {
    try {
      const res = await api.get("/esmp/reviewers");
      const data = res?.data?.data ?? [];
      setReviewers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch reviewers:", err);
      toast.error("Could not load reviewers list");
    }
  }, []);

  useEffect(() => {
    fetchPendingEsmps();
    fetchReviewers();
  }, [fetchPendingEsmps, fetchReviewers]);

  const handleAssignReviewer = async () => {
    if (!reviewerId || !deadline) {
      return toast.error("Please select a reviewer and set a deadline.");
    }

    const toastId = toast.loading("Assigning reviewer...");

    try {
      setSending(true);
      await api.put(`/esmp/${selectedEsmp.id}/assign`, {
        reviewer_id: reviewerId,
        deadline,
      });

      toast.success("Reviewer assigned!", { id: toastId });
      await fetchPendingEsmps();
      setSelectedEsmp(null);
      setReviewerId("");
      setDeadline("");
    } catch (err) {
      console.error("Assignment failed:", err);
      toast.error(err.response?.data?.message || "Failed to assign reviewer", { id: toastId });
    } finally {
      setSending(false);
    }
  };

  const filtered = Array.isArray(pendingEsmps)
    ? pendingEsmps.filter((e) =>
        (e?.esmp_id || "").toLowerCase().includes(search.toLowerCase()) ||
        (e?.district || "").toLowerCase().includes(search.toLowerCase()) ||
        (e?.project_name || "").toLowerCase().includes(search.toLowerCase())
      )
    : [];

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      <main className="flex-1 p-6 space-y-6">
        <div className="flex justify-between items-center bg-gradient-to-r from-green-700 to-green-500 text-white p-5 rounded-xl shadow-md">
          <h2 className="text-2xl font-bold">Pending ESMPs</h2>
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-3 py-2 rounded text-black border focus:ring-2 focus:ring-green-600 outline-none"
            />
            <button
              onClick={fetchPendingEsmps}
              className="px-4 py-2 bg-white text-green-700 rounded-lg font-semibold hover:bg-gray-100"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading pending submissions...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-600">
              <p>{error}</p>
              <button
                onClick={fetchPendingEsmps}
                className="mt-4 px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-500 py-12">
              No pending ESMPs at the moment.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-3 py-2 text-left">ESMP ID</th>
                    <th className="border px-3 py-2 text-left">Project Name</th>
                    <th className="border px-3 py-2 text-left">District</th>
                    <th className="border px-3 py-2 text-left">Submitted By</th>
                    <th className="border px-3 py-2 text-left">Status</th>
                    <th className="border px-3 py-2 text-left">Date</th>
                    <th className="border px-3 py-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item) => (
                    <tr key={item.id} className="hover:bg-green-50">
                      <td className="border px-3 py-2 text-blue-600 font-medium">{item.esmp_id}</td>
                      <td className="border px-3 py-2">{item.project_name}</td>
                      <td className="border px-3 py-2">{item.district}</td>
                      <td className="border px-3 py-2">{item.User?.name || "N/A"}</td>
                      <td className="border px-3 py-2">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            item.status === "Submitted" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td className="border px-3 py-2 text-xs">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </td>
                      <td className="border px-3 py-2 text-center">
                        <button
                          onClick={() => setSelectedEsmp(item)}
                          className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                        >
                          Assign
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selectedEsmp && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
            <div className="bg-white w-full max-w-md p-6 rounded-xl shadow-lg space-y-5">
              <h3 className="text-xl font-bold text-green-700">Assign Reviewer</h3>
              <div className="space-y-2 text-sm bg-gray-50 p-4 rounded">
                <p><b>ESMP ID:</b> {selectedEsmp.esmp_id}</p>
                <p><b>Project:</b> {selectedEsmp.project_name}</p>
                <p><b>District:</b> {selectedEsmp.district}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reviewer</label>
                <select
                  value={reviewerId}
                  onChange={(e) => setReviewerId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Select Reviewer</option>
                  {reviewers.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Deadline</label>
                <input
                  type="datetime-local"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => {
                    setSelectedEsmp(null);
                    setReviewerId("");
                    setDeadline("");
                  }}
                  className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignReviewer}
                  disabled={sending || !reviewerId || !deadline}
                  className={`px-5 py-2 rounded-lg text-white font-medium ${
                    sending || !reviewerId || !deadline
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {sending ? "Assigning..." : "Assign Reviewer"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
